/** POI markers (icon + label) as an HTML overlay, standing ON the roof of the
 *  building they are in. Item 0371.
 *
 *  WHY NOT A SYMBOL LAYER ANY MORE. Until 0371 the markers were a MapLibre
 *  `symbol` layer, and MapLibre draws symbols at ground level: there is no
 *  symbol elevation property in 5.x, and maplibregl.Marker has no altitude
 *  either. Venue units are extruded (5 m on Premià), so in a pitched view every
 *  roof is drawn screen-UP from its own marker, and the marker lands on the
 *  near edge of its roof or on the NEIGHBOUR's roof. The customer saw exactly
 *  that ("Hippie Chic Shop" sitting on The Learning Kingdom's roof), and, worse,
 *  tapping it opened the neighbour (see pickPoi below). The POI points were
 *  correct, inside their own footprints; it was parallax, not a centroid bug.
 *
 *  So each marker is projected here at the altitude of the roof under its
 *  point, through the same two calls the three.js model layer uses
 *  (transform.getMatrixForModel + getProjectionDataForCustomLayer().mainMatrix).
 *  At altitude 0 this is bit-for-bit map.project(). Being DOM above the canvas,
 *  the markers also can no longer be painted over by 3D models or the route.
 *
 *  What the symbol layer gave us for free is kept on purpose:
 *    - collision, in the same terms: higher `priority` placed first; a marker
 *      whose icon collides is hidden whole; a label that collides is dropped
 *      and the icon stays (it was `text-optional`);
 *    - floor filtering (POIs without a floor are always shown);
 *    - looks: the same 96 px disc at 0.65 x 1/2 = 31 px, 13 px label, white halo.
 *  Plus one thing it lacked: the selected POI is placed first and never hidden.
 */
import { placeholderSrc, poiMarkerSrc } from "./logos.js";

const ICON = 31; // px; the old icon-size 0.65 of a 96 px image at pixelRatio 2
const R = ICON / 2;
const LABEL_GAP = 4; // icon bottom -> label top
const COLLIDE_PAD = 2;
const OFFSCREEN = 60; // px margin outside the viewport still laid out

/** Extruded kinds a marker can stand on. Decorations (trees, boats) are not
 *  buildings: a POI next to a planter must not jump onto it. */
export const ROOF_KINDS = new Set(["unit", "shell", "slab", "zone"]);

function ringContains(ring, x, y) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function polygonContains(rings, x, y) {
  if (!rings?.length || !ringContains(rings[0], x, y)) return false;
  for (let h = 1; h < rings.length; h++) if (ringContains(rings[h], x, y)) return false;
  return true;
}

export function geometryContains(geometry, x, y) {
  if (geometry?.type === "Polygon") return polygonContains(geometry.coordinates, x, y);
  if (geometry?.type === "MultiPolygon") {
    return geometry.coordinates.some((rings) => polygonContains(rings, x, y));
  }
  return false;
}

const onFloor = (itemFloor, floorId) => itemFloor == null || itemFloor === floorId;

/** Metres above ground of the roof the POI stands on: the tallest extrusion
 *  shown with the POI's floor whose footprint contains the point, else 0.
 *
 *  Containment, not the POI's linked feature_id: the point is what the visitor
 *  sees, and a few authored POIs sit outside the unit they are linked to (on
 *  Premià, Isabella Showroom's point is inside Pepco). Lifting those to a roof
 *  they are not over would leave them floating in mid-air above the street.
 *
 *  `extrusions`: [{ geometry, floorId, height }] where height is the top. */
export function roofAltitude(poi, extrusions) {
  let top = 0;
  for (const e of extrusions) {
    if (!(e.height > top)) continue;
    if (!onFloor(e.floorId, poi.floor_id)) continue;
    if (geometryContains(e.geometry, poi.lng, poi.lat)) top = e.height;
  }
  return top;
}

/** Normalise a viewer FeatureCollection into roof candidates. */
export function extrusionsFromFC(featuresFC) {
  return (featuresFC?.features ?? [])
    .filter((f) => ROOF_KINDS.has(f.properties?.kind))
    .map((f) => ({
      geometry: f.geometry,
      floorId: f.properties.floor_id ?? null,
      height: Number(f.properties.height) || 0,
    }));
}

const overlaps = (a, b) => a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
const collides = (rect, placed) => placed.some((p) => overlaps(rect, p));
const pad = (r, p) => [r[0] - p, r[1] - p, r[2] + p, r[3] + p];

const LABEL_CSS =
  "position:absolute;left:0;transform:translateX(-50%);width:max-content;max-width:8em;" +
  "text-align:center;font:400 13px/1.15 system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;" +
  "color:#1f2430;white-space:normal;overflow-wrap:break-word;" +
  "text-shadow:0 0 2px #fff,0 0 2px #fff,0 0 3px #fff,0 0 3px #fff,0 0 4px #fff;";

/** Create the overlay. Returns a controller; nothing is shown until setPois().
 *
 *  `interactive: false` (the editor's 3D preview) makes every marker
 *  click-through, so editing clicks reach the map untouched. */
export function createPoiMarkers(map, { interactive = true } = {}) {
  const root = document.createElement("div");
  root.className = "poi-markers";
  // isolation: the markers rank themselves with z-index; without their own
  // stacking context those values leak out and paint over whatever UI floats
  // over the map (viewer menu z-20, editor toolbar). Item 0373 isolated the
  // viewer's map wrapper for the same reason; this covers every embedding.
  root.style.cssText =
    "position:absolute;left:0;top:0;width:0;height:0;pointer-events:none;isolation:isolate;";
  // Directly after the canvas: below MapLibre Markers ("You are here"), which
  // are appended later and answer the one question a POI never should.
  const container = map.getCanvasContainer();
  container.insertBefore(root, map.getCanvas().nextSibling);

  let items = []; // in placement order
  let floorId = null;
  let selected = null;
  let visible = true;
  let extrusions = [];

  function order() {
    items.sort(
      (a, b) =>
        (b.poi.slug === selected) - (a.poi.slug === selected) ||
        (b.poi.priority ?? 0) - (a.poi.priority ?? 0) ||
        a.index - b.index,
    );
  }

  function build(pois, { name, categoriesById }) {
    for (const it of items) it.el.remove();
    items = pois.map((poi, index) => {
      const el = document.createElement("div");
      el.dataset.poiSlug = poi.slug;
      el.style.cssText =
        "position:absolute;left:0;top:0;width:0;height:0;opacity:0;" +
        "transition:opacity .2s;will-change:transform;pointer-events:none;" +
        (interactive ? "cursor:pointer;" : "");

      const img = document.createElement("img");
      img.alt = "";
      img.draggable = false;
      img.width = img.height = ICON;
      img.src = placeholderSrc();
      // max-width:none: Tailwind's preflight sets img{max-width:100%}, and 100%
      // of this 0-wide anchor is 0 -- the disc vanished, leaving bare labels.
      img.style.cssText = `position:absolute;left:${-R}px;top:${-R}px;width:${ICON}px;height:${ICON}px;max-width:none;max-height:none;display:block;user-select:none;-webkit-user-drag:none;`;
      poiMarkerSrc(poi, categoriesById).then((src) => {
        if (src) img.src = src;
      });

      const label = document.createElement("div");
      label.textContent = name(poi);
      label.style.cssText = LABEL_CSS + `top:${R + LABEL_GAP}px;`;

      el.append(img, label);
      root.appendChild(el);
      return {
        poi, index, el, label,
        alt: roofAltitude(poi, extrusions),
        lw: 0, lh: 0, // label box, measured on first layout
        shown: false, labelShown: false, icon: null, text: null,
      };
    });
    // Measure every label in one pass, after all are in the DOM: one reflow,
    // not one per marker interleaved with transform writes.
    for (const it of items) measure(it);
    order();
  }

  function measure(it) {
    it.lw = it.label.offsetWidth;
    it.lh = it.label.offsetHeight;
  }

  function hide(it) {
    if (!it.shown) return;
    it.shown = false;
    it.icon = it.text = null;
    it.el.style.opacity = "0";
    it.el.style.pointerEvents = "none";
  }

  function update() {
    if (!visible) {
      items.forEach(hide);
      return;
    }
    const tr = map.transform;
    const P = tr.getProjectionDataForCustomLayer().mainMatrix;
    const W = tr.width;
    const H = tr.height;
    const placed = [];
    const n = items.length;

    items.forEach((it, rank) => {
      const { poi } = it;
      if (!onFloor(poi.floor_id, floorId)) return hide(it);

      // Clip-space position of the roof point (see header).
      const M = tr.getMatrixForModel([poi.lng, poi.lat], it.alt);
      const x0 = M[12], y0 = M[13], z0 = M[14], w0 = M[15];
      const cw = P[3] * x0 + P[7] * y0 + P[11] * z0 + P[15] * w0;
      if (!(cw > 0)) return hide(it); // behind the camera
      const x = ((P[0] * x0 + P[4] * y0 + P[8] * z0 + P[12] * w0) / cw + 1) * 0.5 * W;
      const y = (1 - (P[1] * x0 + P[5] * y0 + P[9] * z0 + P[13] * w0) / cw) * 0.5 * H;
      if (x < -OFFSCREEN || x > W + OFFSCREEN || y < -OFFSCREEN || y > H + OFFSCREEN) {
        return hide(it);
      }

      const icon = [x - R, y - R, x + R, y + R];
      const isSelected = poi.slug === selected;
      if (!isSelected && collides(pad(icon, COLLIDE_PAD), placed)) return hide(it);
      placed.push(icon);

      if (!it.lw) measure(it); // map was hidden (0x0) at build time
      const top = y + R + LABEL_GAP;
      const text = it.lw ? [x - it.lw / 2, top, x + it.lw / 2, top + it.lh] : null;
      const labelFits = text && !collides(pad(text, COLLIDE_PAD), placed);
      if (labelFits) placed.push(text);

      it.icon = icon;
      it.text = labelFits ? text : null;
      if (labelFits !== it.labelShown) {
        it.labelShown = labelFits;
        it.label.style.visibility = labelFits ? "visible" : "hidden";
      }
      it.el.style.transform = `translate3d(${Math.round(x)}px,${Math.round(y)}px,0)`;
      it.el.style.zIndex = String(n - rank);
      if (!it.shown) {
        it.shown = true;
        it.el.style.opacity = "1";
        if (interactive) it.el.style.pointerEvents = "auto";
      }
    });
  }

  map.on("move", update);
  map.on("resize", update);

  return {
    /** (Re)build every marker. `name(poi)` returns the display label. */
    setPois(pois, { name, categoriesById = {}, extrusions: ex } = {}) {
      if (ex) extrusions = ex;
      build(pois, { name, categoriesById });
      update();
    },
    setFloor(id) {
      floorId = id;
      update();
    },
    setSelected(slug) {
      selected = slug ?? null;
      order();
      update();
    },
    setVisible(v) {
      visible = !!v;
      update();
    },
    update,

    /** Which POI a map click hit, or null. A marker always beats whatever is
     *  drawn under it -- that is the whole of the 0371 click fix.
     *
     *  1. The event's own target: a tap that landed ON a marker element.
     *  2. Otherwise the nearest shown marker whose icon or label lies within
     *     `tolerance` px of the point. A finger on a kiosk is not a mouse
     *     pointer; a tap that grazes the edge of a 31 px disc meant the disc. */
    pick(e, tolerance = 0) {
      if (!interactive) return null;
      const target = e.originalEvent?.target;
      if (target && root.contains(target)) {
        const el = target.closest?.("[data-poi-slug]");
        if (el) return el.dataset.poiSlug;
      }
      const { x, y } = e.point;
      let best = null;
      let bestD = Infinity;
      for (const it of items) {
        if (!it.shown) continue;
        const hit = [it.icon, it.text].some(
          (r) => r && x >= r[0] - tolerance && x <= r[2] + tolerance && y >= r[1] - tolerance && y <= r[3] + tolerance,
        );
        if (!hit) continue;
        const cx = (it.icon[0] + it.icon[2]) / 2;
        const cy = (it.icon[1] + it.icon[3]) / 2;
        const d = (cx - x) ** 2 + (cy - y) ** 2;
        if (d < bestD) {
          bestD = d;
          best = it.poi.slug;
        }
      }
      return best;
    },

    destroy() {
      map.off("move", update);
      map.off("resize", update);
      root.remove();
      items = [];
    },
  };
}
